import React, { lazy, Suspense, startTransition, useEffect, useMemo, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { CreditCard, Users } from 'lucide-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import posthog from 'posthog-js';
import { supabase } from './supabase';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ThemeProvider } from './ThemeContext';
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';
import { AdminProvider } from './contexts/AdminContext';
import { isPlatformAdmin } from './config/admin';
import WorkspacePageLayout from './components/layout/WorkspacePageLayout';
import LanguageSync from './components/LanguageSync';
import { Toaster } from '@/components/ui/toaster';
import { setAuthHint, clearAuthHint, hasAuthHint } from './utils/authHint';
import { usePostHogIdentify } from './hooks/usePostHogIdentify';
import { analyticsCapture } from './lib/analytics';

// LandingPage is NOT lazy-loaded because it's the first page visitors see.
// Having it in the main bundle ensures instant load without a progress bar.
import LandingPage from './LandingPage';
const LoginPage = lazy(() => import('./LoginPage'));
const Signup = lazy(() => import('./Signup'));
const Onboarding = lazy(() => import('./Onboarding'));
const HomePage = lazy(() => import('./HomePage'));
const UserProfile = lazy(() => import('./UserProfile'));
const SettingsPage = lazy(() => import('./Setting'));
const OngridPage = lazy(() => import('./Ongrid'));
const AcceptInvite = lazy(() => import('./AcceptInvite'));
const TeamManagement = lazy(() => import('./components/TeamManagement'));
const BillingPage = lazy(() => import('./components/BillingPage'));
const ChatPage = lazy(() => import('./pages/ChatPage').then((m) => ({ default: m.ChatPage })));
const LegalDocPage = lazy(() => import('./pages/legal/LegalDocPage'));
const FreeStudyPage = lazy(() => import('./pages/FreeStudyPage'));

const AdminLayout = lazy(() => import('./pages/admin').then((m) => ({ default: m.AdminLayout })));
const AdminDashboardPage = lazy(() =>
  import('./pages/admin').then((m) => ({ default: m.DashboardPage }))
);
const AdminCustomersPage = lazy(() =>
  import('./pages/admin').then((m) => ({ default: m.CustomersPage }))
);
const AdminCustomerDetailPage = lazy(() =>
  import('./pages/admin').then((m) => ({ default: m.CustomerDetailPage }))
);
const AdminUsersPage = lazy(() => import('./pages/admin').then((m) => ({ default: m.UsersPage })));
const AdminFeedbackPage = lazy(() =>
  import('./pages/admin').then((m) => ({ default: m.FeedbackPage }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: 24 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

type AuthState = {
  user: User | null;
  initializing: boolean;
};

function FullScreenLoading({ label }: { label: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm px-6">
        <div className="rounded-lg border bg-card p-6 shadow-sm">
          <div className="text-sm text-muted-foreground">{label}</div>
          <div className="mt-4 h-2 w-full overflow-hidden rounded bg-muted">
            <div className="h-full w-1/2 animate-pulse rounded bg-primary/60" />
          </div>
        </div>
      </div>
    </div>
  );
}

function useSupabaseAuth(): AuthState {
  const [user, setUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!isMounted) return;
        setUser(session?.user ?? null);
        if (session?.user) {
          setAuthHint();
        } else {
          clearAuthHint();
        }
      } catch {
        if (!isMounted) return;
        setUser(null);
        clearAuthHint();
      } finally {
        if (!isMounted) return;
        setInitializing(false);
      }
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      startTransition(() => {
        setUser(session?.user ?? null);
        setInitializing(false);
      });
      if (event === 'SIGNED_IN' && session?.user) {
        setAuthHint();
        analyticsCapture('user_logged_in', {
          auth_provider: session.user.app_metadata?.provider || 'email',
          has_pending_invite_token: Boolean(localStorage.getItem('pending_invite_token')),
        });
      } else if (event === 'SIGNED_OUT') {
        clearAuthHint();
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return { user, initializing };
}

function RequireAuth({
  user,
  initializing,
  children,
}: {
  user: User | null;
  initializing: boolean;
  children: (user: User) => React.ReactNode;
}) {
  const location = useLocation();
  if (initializing) {
    return <FullScreenLoading label="Loading your session…" />;
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children(user)}</>;
}

function PublicOnly({
  user,
  initializing,
  children,
}: {
  user: User | null;
  initializing: boolean;
  children: React.ReactNode;
}) {
  if (initializing) return <FullScreenLoading label="Loading…" />;
  if (user) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function LandingPageRoute({
  user,
  initializing,
  children,
}: {
  user: User | null;
  initializing: boolean;
  children: React.ReactNode;
}) {
  if (user) return <Navigate to="/app" replace />;
  if (!initializing) return <>{children}</>;
  if (hasAuthHint()) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function RequireAdmin({ user, children }: { user: User; children: React.ReactNode }) {
  if (!isPlatformAdmin(user.email)) {
    return <Navigate to="/app" replace />;
  }
  return <>{children}</>;
}

function AdminRoutes({ user }: { user: User }) {
  return (
    <AdminProvider user={user}>
      <Routes>
        <Route element={<AdminLayout />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="customers" element={<AdminCustomersPage />} />
          <Route path="customers/:customerId" element={<AdminCustomerDetailPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="feedback" element={<AdminFeedbackPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminProvider>
  );
}

function InviteRoute({ user }: { user: User | null }) {
  const navigate = useNavigate();
  const { token: tokenParam } = useParams();
  const [searchParams] = useSearchParams();

  const token = tokenParam ?? searchParams.get('invite') ?? searchParams.get('token') ?? '';

  if (!token) return <Navigate to="/" replace />;

  return (
    <Suspense fallback={<FullScreenLoading label="Loading…" />}>
      <AcceptInvite
        token={token}
        onGoToLogin={() => navigate('/login')}
        onSuccess={(orgId) => navigate(`/app/org/${orgId}`, { replace: true })}
      />
    </Suspense>
  );
}

function OnboardingRoute({ user, onComplete }: { user: User; onComplete: () => void }) {
  const { needsOnboarding, loading } = useOrganization();

  if (loading) return <FullScreenLoading label="Loading…" />;
  if (!needsOnboarding) return <Navigate to="/app" replace />;

  return (
    <Suspense fallback={<FullScreenLoading label="Loading…" />}>
      <Onboarding user={user} onComplete={onComplete} />
    </Suspense>
  );
}

function AppIndex() {
  const { currentOrg, loading, needsOnboarding } = useOrganization();
  if (loading) return <FullScreenLoading label="Loading workspace…" />;
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;
  if (currentOrg?.id) return <Navigate to={`/app/org/${currentOrg.id}`} replace />;
  return <Navigate to="/onboarding" replace />;
}

function OrgGuard({ children }: { children: React.ReactNode }) {
  const { orgId } = useParams();
  const { currentOrg, loading, orgLoading, switchOrg } = useOrganization();
  const [switchError, setSwitchError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    if (loading || orgLoading) return;
    if (currentOrg?.id === orgId) return;

    setSwitchError(null);
    switchOrg(orgId).catch((e) => {
      setSwitchError(e instanceof Error ? e.message : 'Failed to switch organization');
    });
  }, [orgId, loading, orgLoading, currentOrg?.id, switchOrg]);

  if (!orgId) return <Navigate to="/app" replace />;
  if (loading || orgLoading || (currentOrg && currentOrg.id !== orgId)) {
    return <FullScreenLoading label="Loading organization…" />;
  }
  if (switchError) return <Navigate to="/app" replace />;

  return <>{children}</>;
}

function ChatRoute({ user }: { user: User }) {
  return <ChatPage user={user} />;
}

function WorkspaceRoutes({ user }: { user: User }) {
  const { currentOrg } = useOrganization();

  // Identify user and associate with organization in PostHog
  usePostHogIdentify(user, currentOrg);

  return (
    <Routes>
      <Route path="/app" element={<AppIndex />} />

      <Route
        path="/app/org/:orgId/chat"
        element={
          <OrgGuard>
            <ChatRoute user={user} />
          </OrgGuard>
        }
      />

      <Route
        path="/app/org/:orgId/chat/:conversationId"
        element={
          <OrgGuard>
            <ChatRoute user={user} />
          </OrgGuard>
        }
      />

      <Route
        path="/app/org/:orgId"
        element={
          <OrgGuard>
            <HomeRoute user={user} />
          </OrgGuard>
        }
      />

      <Route
        path="/app/org/:orgId/profile"
        element={
          <OrgGuard>
            <ProfileRoute user={user} />
          </OrgGuard>
        }
      />

      <Route
        path="/app/org/:orgId/settings"
        element={
          <OrgGuard>
            <SettingsRoute user={user} />
          </OrgGuard>
        }
      />

      <Route
        path="/app/org/:orgId/team"
        element={
          <OrgGuard>
            <TeamRoute user={user} />
          </OrgGuard>
        }
      />

      <Route
        path="/app/org/:orgId/billing"
        element={
          <OrgGuard>
            <BillingRoute user={user} />
          </OrgGuard>
        }
      />

      <Route
        path="/app/org/:orgId/projects/new/ongrid"
        element={
          <OrgGuard>
            <OngridNewRoute user={user} />
          </OrgGuard>
        }
      />

      <Route
        path="/app/org/:orgId/projects/new/ongrid/:section"
        element={
          <OrgGuard>
            <OngridNewRoute user={user} />
          </OrgGuard>
        }
      />

      <Route
        path="/app/org/:orgId/projects/:projectId/ongrid"
        element={
          <OrgGuard>
            <OngridProjectRoute user={user} />
          </OrgGuard>
        }
      />

      <Route
        path="/app/org/:orgId/projects/:projectId/ongrid/:section"
        element={
          <OrgGuard>
            <OngridProjectRoute user={user} />
          </OrgGuard>
        }
      />

      <Route path="/app/org/:orgId/projects/:projectId" element={<ProjectRedirect />} />

      <Route path="/app/org/:orgId/projects/:projectId/*" element={<ProjectRedirect />} />

      <Route path="/app/org/:orgId/projects" element={<ProjectsListRedirect />} />

      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

function useWorkspaceActions() {
  const navigate = useNavigate();
  const { orgId } = useParams();

  const handleLogout = async () => {
    clearAuthHint();
    posthog.reset(); // Clear PostHog identity on logout
    await supabase.auth.signOut();
    navigate('/', { replace: true });
  };

  const goHome = () => {
    if (orgId) navigate(`/app/org/${orgId}`);
    else navigate('/app');
  };

  return { navigate, orgId, handleLogout, goHome };
}

function HomeRoute({ user }: { user: User }) {
  const { navigate, orgId, handleLogout } = useWorkspaceActions();

  return (
    <HomePage
      user={user}
      onLogout={handleLogout}
      onChoosePlan={() => navigate(`/app/org/${orgId}/billing`)}
      onGoToProfile={() => navigate(`/app/org/${orgId}/profile`)}
      onGoToSettings={() => navigate(`/app/org/${orgId}/settings`)}
      onGoToTeam={() => navigate(`/app/org/${orgId}/team`)}
      onGoToBilling={() => navigate(`/app/org/${orgId}/billing`)}
      onGoToChat={() => navigate(`/app/org/${orgId}/chat`)}
      onGoToAdmin={isPlatformAdmin(user.email) ? () => navigate('/admin') : undefined}
      onGoToOngrid={(projectId) => {
        const base = `/app/org/${orgId}/projects`;
        if (projectId) navigate(`${base}/${projectId}/ongrid`);
        else navigate(`${base}/new/ongrid`);
      }}
      onOrgChanged={(nextOrgId) => navigate(`/app/org/${nextOrgId}`)}
    />
  );
}

function ProfileRoute({ user }: { user: User }) {
  const { goHome, handleLogout } = useWorkspaceActions();
  return <UserProfile user={user} onGoHome={goHome} onLogout={handleLogout} />;
}

function SettingsRoute({ user }: { user: User }) {
  const { navigate, orgId, goHome, handleLogout } = useWorkspaceActions();
  return (
    <SettingsPage
      user={user}
      onGoHome={goHome}
      onLogout={handleLogout}
      onGoBilling={() => navigate(`/app/org/${orgId}/billing`)}
    />
  );
}

function TeamRoute({ user }: { user: User }) {
  const { goHome, handleLogout } = useWorkspaceActions();
  return (
    <WorkspacePageLayout
      title="Gestion de l'équipe"
      icon={<Users className="h-4 w-4" />}
      onGoHome={goHome}
      onLogout={handleLogout}
    >
      <TeamManagement />
    </WorkspacePageLayout>
  );
}

function BillingRoute({ user }: { user: User }) {
  const { goHome, handleLogout } = useWorkspaceActions();
  return (
    <WorkspacePageLayout
      title="Facturation & abonnement"
      icon={<CreditCard className="h-4 w-4" />}
      onGoHome={goHome}
      onLogout={handleLogout}
    >
      <BillingPage />
    </WorkspacePageLayout>
  );
}

function OngridNewRoute({ user }: { user: User }) {
  const { navigate, orgId, goHome } = useWorkspaceActions();
  const { section } = useParams();

  return (
    <OngridPage
      user={user}
      onGoHome={goHome}
      projectId={null}
      initialSection={section || 'data'}
      onSectionChange={(newSection: string) =>
        navigate(`/app/org/${orgId}/projects/new/ongrid/${newSection}`, {
          replace: true,
        })
      }
      onProjectIdResolved={(pid: string, currentSection?: string) =>
        navigate(`/app/org/${orgId}/projects/${pid}/ongrid/${currentSection || 'data'}`, {
          replace: true,
        })
      }
    />
  );
}

function OngridProjectRoute({ user }: { user: User }) {
  const { navigate, orgId, goHome } = useWorkspaceActions();
  const { projectId, section } = useParams();
  const normalized = projectId && projectId !== 'new' ? projectId : null;

  return (
    <OngridPage
      user={user}
      onGoHome={goHome}
      projectId={normalized}
      initialSection={section || 'data'}
      onSectionChange={(newSection: string) =>
        navigate(`/app/org/${orgId}/projects/${projectId}/ongrid/${newSection}`, { replace: true })
      }
      onProjectIdResolved={(pid: string, currentSection?: string) =>
        navigate(`/app/org/${orgId}/projects/${pid}/ongrid/${currentSection || 'data'}`, {
          replace: true,
        })
      }
    />
  );
}

function ProjectRedirect() {
  const { orgId, projectId } = useParams();
  return <Navigate to={`/app/org/${orgId}/projects/${projectId}/ongrid`} replace />;
}

function ProjectsListRedirect() {
  const { orgId } = useParams();
  return <Navigate to={`/app/org/${orgId}`} replace />;
}

export default function App() {
  const { user, initializing } = useSupabaseAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const inviteRedirect = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const invite = params.get('invite') ?? params.get('token');
    if (!invite) return null;
    if (location.pathname.startsWith('/invite')) return null;
    return { invite };
  }, [location.pathname, location.search]);

  const checkoutCleanup = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get('checkout');
    if (!checkout) return null;
    params.delete('checkout');
    const next = `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    return { checkout, next };
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!inviteRedirect) return;
    navigate(`/invite?invite=${encodeURIComponent(inviteRedirect.invite)}`, {
      replace: true,
    });
  }, [inviteRedirect, navigate]);

  useEffect(() => {
    if (!checkoutCleanup) return;
    navigate(checkoutCleanup.next, { replace: true });
  }, [checkoutCleanup, navigate]);

  return (
    <ErrorBoundary>
      <Routes>
        <Route
          path="/"
          element={
            <LandingPageRoute user={user} initializing={initializing}>
              <LandingPage
                onGoToLogin={() => navigate('/login')}
                onGoToSignup={() => navigate('/signup')}
              />
            </LandingPageRoute>
          }
        />

        <Route
          path="/free-study"
          element={
            <LandingPageRoute user={user} initializing={initializing}>
              <Suspense fallback={<FullScreenLoading label="Loading…" />}>
                <FreeStudyPage />
              </Suspense>
            </LandingPageRoute>
          }
        />

        <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
        <Route
          path="/legal/:docId"
          element={
            <Suspense fallback={<FullScreenLoading label="Loading…" />}>
              <LegalDocPage />
            </Suspense>
          }
        />

        <Route
          path="*"
          element={
            <Suspense fallback={<FullScreenLoading label="Loading…" />}>
              <Routes>
                <Route
                  path="/login"
                  element={
                    <PublicOnly user={user} initializing={initializing}>
                      <LoginPage
                        onGoToSignup={() => navigate('/signup')}
                        onGoToLanding={() => navigate('/')}
                        onLogin={() => navigate('/app', { replace: true })}
                      />
                    </PublicOnly>
                  }
                />

                <Route
                  path="/signup"
                  element={
                    <PublicOnly user={user} initializing={initializing}>
                      <Signup
                        onGoToLogin={() => navigate('/login')}
                        onGoToLanding={() => navigate('/')}
                      />
                    </PublicOnly>
                  }
                />

                <Route
                  path="/onboarding"
                  element={
                    <RequireAuth user={user} initializing={initializing}>
                      {(authedUser) => (
                        <QueryClientProvider client={queryClient}>
                          <ThemeProvider user={authedUser}>
                            <OrganizationProvider user={authedUser}>
                              <OnboardingRoute
                                user={authedUser}
                                onComplete={() => navigate('/app', { replace: true })}
                              />
                            </OrganizationProvider>
                          </ThemeProvider>
                        </QueryClientProvider>
                      )}
                    </RequireAuth>
                  }
                />

                <Route path="/invite" element={<InviteRoute user={user} />} />
                <Route path="/invite/:token" element={<InviteRoute user={user} />} />

                <Route
                  path="/admin/*"
                  element={
                    <RequireAuth user={user} initializing={initializing}>
                      {(authedUser) => (
                        <QueryClientProvider client={queryClient}>
                          <ThemeProvider user={authedUser}>
                            <RequireAdmin user={authedUser}>
                              <AdminRoutes user={authedUser} />
                            </RequireAdmin>
                          </ThemeProvider>
                        </QueryClientProvider>
                      )}
                    </RequireAuth>
                  }
                />

                <Route
                  path="*"
                  element={
                    <RequireAuth user={user} initializing={initializing}>
                      {(authedUser) => (
                        <QueryClientProvider client={queryClient}>
                          <ThemeProvider user={authedUser}>
                            <LanguageSync user={authedUser} />
                            <OrganizationProvider user={authedUser}>
                              <WorkspaceRoutes user={authedUser} />
                            </OrganizationProvider>
                          </ThemeProvider>
                        </QueryClientProvider>
                      )}
                    </RequireAuth>
                  }
                />
              </Routes>
            </Suspense>
          }
        />
      </Routes>

      <Toaster />
    </ErrorBoundary>
  );
}
